import { SvgIconComponent } from '@mui/icons-material';
import { Box, Button, Stack, Typography, useTheme } from '@mui/material';
import { useField } from 'formik';

type NameKey = string | number | symbol;

interface Props<TName extends NameKey = string> {
  name: TName;
  disabled?: boolean;
  horizontal?: boolean;
  options: {
    value: string;
    title: string;
    icon: SvgIconComponent;
    description: string;
  }[];
}

function ButtonsSelect<TName extends NameKey = string>({
  name,
  disabled,
  horizontal,
  options,
}: Props<TName>) {
  const theme = useTheme();
  const formikField = useField(name.toString());
  const [field, , helpers] = formikField;

  return (
    <Stack
      gap={1}
      width="100%"
      sx={{
        flexDirection: horizontal ? 'row' : 'column',
      }}>
      {options.map(option => {
        const isActive = field.value === option.value;
        const backgroundColor = isActive
          ? theme.palette.accent.main
          : theme.palette.background.default;
        const Icon = option.icon;
        return (
          <Button
            disableRipple
            sx={{
              borderRadius: '10px',
              width: horizontal
                ? `${Math.floor(100 / options.length)}%`
                : '100%',
              backgroundColor,
              border: `1px solid ${
                isActive
                  ? theme.palette.darkGrey.main
                  : theme.palette.darkerLightGrey.main
              }`,
              paddingX: '20px',
              paddingY: '30px',
              textAlign: 'left',
              opacity: disabled ? 0.5 : 1,
              ':hover': {
                backgroundColor,
                filter: 'brightness(0.95)',
              },
            }}
            key={option.value}
            variant="text"
            disabled={disabled}
            onClick={() => helpers.setValue(option.value)}>
            <Box
              display="flex"
              alignItems={{
                xs: 'flex-start',
                sm: 'center',
              }}
              gap={{
                xs: horizontal ? 1 : 2.5,
                sm: 2.5,
              }}
              flexDirection={{
                xs: 'column',
                sm: 'row',
              }}
              flex={1}>
              <Icon
                sx={{
                  fontSize: 24,
                }}
                htmlColor={theme.palette.darkGrey.main}
              />
              <Stack>
                <Typography variant="h3" color="text.primary">
                  {option.title}
                </Typography>
                <Typography variant="body2" color="text.primary">
                  {option.description}
                </Typography>
              </Stack>
            </Box>
          </Button>
        );
      })}
    </Stack>
  );
}

export default ButtonsSelect;
